import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { AccordionSection, Card } from '@aimsos/ui';

import { resolveExposedColumns } from '../hooks/useWidgetBuilderForm.js';
import DatasetPicker from './DatasetPicker.jsx';
import DataSourceTypePicker from './DataSourceTypePicker.jsx';
import EntityPicker from './EntityPicker.jsx';
import ExposedColumnsSection from './ExposedColumnsSection.jsx';
import FilterConfigSection from './FilterConfigSection.jsx';
import InlineNote from './InlineNote.jsx';
import ResetDownstreamConfigModal from './ResetDownstreamConfigModal.jsx';

/**
 * Step `1 Data` (CT-5191 · CT-5193 · CT-5194).
 *
 * Orchestration only — it decides what is visible, and each section owns its own
 * behaviour. One rule, applied twice:
 *
 * **Everything past the source picker is entity-path only.** A governed dataset
 * is a pre-built query: it already carries its filters and declares its own
 * shape, so offering `FILTERS` or an operation there would let the user
 * contradict the dataset they just picked. The design source of truth agrees —
 * choosing `Existing dataset` ends the Data tab at the dataset grid.
 */
function DataStep({ form, sourceColumns, datasetError }) {
  const { t } = useTranslation('dashboards');
  const {
    dataset,
    pendingChange,
    requestSourceType,
    requestDataset,
    requestEntity,
    confirmPendingChange,
    cancelPendingChange,
    addFilter,
    updateFilter,
    removeFilter,
    setExposedColumns,
  } = form;

  const {
    columns, unsupportedColumns, columnsByFieldUuid, isLoading, error, refetch,
  } = sourceColumns;

  const isDatasetPath = dataset.sourceType === 'dataset';
  const isEntityPath = dataset.sourceType === 'entity';
  const hasSource = Boolean(dataset.sourceId);

  const exposedColumns = useMemo(
    () => resolveExposedColumns(dataset.exposedColumns),
    [dataset.exposedColumns],
  );

  return (
    <>
      {/* Publishing the selection as a DATASET is what lets the widget name
          its columns, so a refusal belongs here, beside the selection that
          caused it — not in a toast on the next tab (CT-5725). */}
      {datasetError ? (
        <InlineNote tone="warning">
          {t('widgetBuilder.data.datasetError', {
            reason: datasetError.message,
            defaultValue: datasetError.message,
          })}
        </InlineNote>
      ) : null}

      <Card className="flex flex-col gap-1 p-5">
        <AccordionSection
          title={t('widgetBuilder.data.sourceType.title')}
          titleSize="xs"
          defaultOpen
        >
          <div className="pb-4 pt-3">
            <DataSourceTypePicker value={dataset.sourceType} onChange={requestSourceType} />
          </div>
        </AccordionSection>

        {isDatasetPath ? (
          <DatasetPicker value={dataset.datasetId} onSelect={requestDataset} />
        ) : null}

        {isEntityPath ? (
          <EntityPicker value={dataset.compositeEntityUuid} onSelect={requestEntity} />
        ) : null}

        {isEntityPath && hasSource ? (
          <FilterConfigSection
            filters={dataset.filters}
            columns={columns}
            columnsByFieldUuid={columnsByFieldUuid}
            columnsLoading={isLoading}
            columnsError={error}
            onRetryColumns={refetch}
            onAdd={addFilter}
            onChange={updateFilter}
            onRemove={removeFilter}
          />
        ) : null}

        {/*
          The columns the widget may use (CT-5725).

          Since the selection is published as a plain RECORD_SET dataset,
          this step no longer decides *what is done* with them: the
          operations and the groupings belong to the type's slots and live in
          Configure, where each slot asks for its own operation and column.
          What is left here is the source, what it is narrowed by, and which
          columns travel.
        */}
        {isEntityPath && hasSource ? (
          <ExposedColumnsSection
            selectedFieldUuids={exposedColumns}
            columns={columns}
            unsupportedColumns={unsupportedColumns}
            columnsByFieldUuid={columnsByFieldUuid}
            columnsLoading={isLoading}
            columnsError={error}
            onRetryColumns={refetch}
            onChange={setExposedColumns}
          />
        ) : null}

      </Card>

      <ResetDownstreamConfigModal
        pendingChange={pendingChange}
        onCancel={cancelPendingChange}
        onConfirm={confirmPendingChange}
      />
    </>
  );
}

const datasetShape = PropTypes.shape({
  sourceType: PropTypes.string,
  sourceId: PropTypes.string,
  datasetId: PropTypes.string,
  datasetShape: PropTypes.string,
  entityRef: PropTypes.string,
  compositeEntityUuid: PropTypes.string,
  operationType: PropTypes.string,
  filters: PropTypes.array,
  measures: PropTypes.array,
  groupers: PropTypes.array,
  exposedColumns: PropTypes.arrayOf(PropTypes.string),
});

DataStep.propTypes = {
  /** Why the selection could not be published as a dataset, if it could not. */
  datasetError: PropTypes.object,
  form: PropTypes.shape({
    dataset: datasetShape.isRequired,
    pendingChange: PropTypes.object,
    requestSourceType: PropTypes.func.isRequired,
    requestDataset: PropTypes.func.isRequired,
    requestEntity: PropTypes.func.isRequired,
    confirmPendingChange: PropTypes.func.isRequired,
    cancelPendingChange: PropTypes.func.isRequired,
    addFilter: PropTypes.func.isRequired,
    updateFilter: PropTypes.func.isRequired,
    removeFilter: PropTypes.func.isRequired,
    setExposedColumns: PropTypes.func.isRequired,
  }).isRequired,
  sourceColumns: PropTypes.shape({
    columns: PropTypes.array,
    unsupportedColumns: PropTypes.arrayOf(PropTypes.object),
    columnsByFieldUuid: PropTypes.object,
    isLoading: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    refetch: PropTypes.func,
  }).isRequired,
};

export default DataStep;
